import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import AddCollaboratorsAsAdminViewModel from './AddCollaboratorsAsAdminViewModel'
import {
  CustomSelect,
  RenderSearchItemProps,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  Form,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
} from 'components/inputs'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import styles from './AddCollaboratorsAsAdmin.sass'
import Icon from 'components/Icon'
import ErrorBlock from 'components/ErrorBlock'
import Avatar from 'components/Avatar'
import Member from 'iam/models/Member'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'

interface AddCollaboratorsAsAdminProps {
  vm: AddCollaboratorsAsAdminViewModel
}

export default observer(function AddCollaboratorsAsAdminDialog({
  vm,
}: AddCollaboratorsAsAdminProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle align="left" compact>
            <span className={styles.title}>Add a collaborator</span>
          </ModalSheetContentTitle>
          <ModalSheetContentDescription align="left" compact>
            <span className={styles.description}>
              The collaborator will have access to the job
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          {!vm.isDraftJob && !vm.canOnlyAddClient && (
            <FormRow className={styles.collaboratorTypeFilterRow}>
              <div className={styles.filterTitle}>
                What would you like to add?
              </div>
              <RadioButtonGroup size="big">
                <RadioButtonItem
                  onClick={vm.setAddingClient}
                  active={vm.isAddingClient}
                  dataTest="add-job-client"
                >
                  Client
                </RadioButtonItem>
                <RadioButtonItem
                  onClick={vm.setAddingPro}
                  active={vm.isAddingPro}
                  dataTest="add-job-pro"
                >
                  Pro
                </RadioButtonItem>
              </RadioButtonGroup>
            </FormRow>
          )}
          {vm.isAddingClient && <AddClientForm vm={vm} />}
          {vm.isAddingPro && !vm.canOnlyAddClient && <AddProForm vm={vm} />}
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.submit.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.submit.error} />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            loading={vm.submit.pending}
            data-test="add-job-collaborator-as-admin-submit-button"
            onClick={vm.submit}
            disabled={vm.disabled}
          >
            {'Add Collaborator'}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const AddClientForm = observer(function AddClientForm({
  vm,
}: {
  vm: AddCollaboratorsAsAdminViewModel
}) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.isClientTeamJob
      ? vm.fetchTeamMembers
      : vm.fetchWorkspaceMembers,
  })

  const clientError = vm.validation.getError('collaborator')
  const reasonError = vm.validation.getError('reason')

  return (
    <>
      <Form className={styles.row}>
        <FormGroupContainer
          field="collaborator"
          model={vm}
          className={styles.selectCollaboratorContainer}
          error={clientError}
        >
          <div className={styles.addCollaboratorLabel}>Client</div>
          <CustomSelect
            {...selectProps}
            multi={false}
            icon={<Icon name="dropdown" className={cx(styles.icon)} />}
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={
              vm.isClientTeamJob
                ? 'Search team members'
                : 'Search any available'
            }
            value={vm.collaborator}
            onChange={vm.setCollaborator}
            renderSearchItem={(props) => (
              <AddCollaboratorSearchItem {...props} />
            )}
            data-test="add-job-collaborator-as-admin-dropdown"
          />
        </FormGroupContainer>
        <FormRow className={styles.row}>
          <div className={styles.reasonLabel}>Reason</div>
          <TextAreaContainer
            rows={4}
            model={vm}
            field="reason"
            name="reason"
            maxLength={vm.MAX_REASON_LENGTH}
            error={reasonError}
            status={reasonError ? 'danger' : 'default'}
            placeholder={vm.reasonHelpText}
            help={`${vm.reasonCharsRemaining} characters remaining`}
            data-test="add-job-collaborator-as-admin-reason-input"
          />
        </FormRow>
      </Form>
    </>
  )
})

const AddProForm = observer(function AddProForm({
  vm,
}: {
  vm: AddCollaboratorsAsAdminViewModel
}) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.canDisplayAllPros
      ? vm.fetchWorkspaceMembers
      : vm.fetchQualifiedPros,
  })

  const clientError = vm.validation.getError('collaborator')
  const reasonError = vm.validation.getError('reason')

  return (
    <>
      <FormRow className={styles.row}>
        <div className={styles.filterTitle}>
          What kind of Pro are you looking for?
        </div>
        <RadioButtonGroup size="big">
          <RadioButtonItem
            onClick={vm.setDisplayQualifiedPros}
            active={vm.canDisplayQualifiedPros}
            dataTest="add-job-collaborator-select-qualified"
          >
            Qualified only
          </RadioButtonItem>
          <RadioButtonItem
            onClick={vm.setDisplayAllPros}
            active={vm.canDisplayAllPros}
            dataTest="add-job-collaborator-select-all"
          >
            Any available
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormRow>
      <Form className={styles.row}>
        <FormGroupContainer
          field="collaborator"
          model={vm}
          className={styles.selectCollaboratorContainer}
          error={clientError}
          help={vm.proSelectionHelpText}
        >
          <div className={styles.addCollaboratorLabel}>Pro</div>
          <CustomSelect
            {...selectProps}
            multi={false}
            icon={<Icon name="dropdown" className={cx(styles.icon)} />}
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={
              vm.canDisplayAllPros
                ? 'Search any available'
                : 'Search qualified only'
            }
            value={vm.collaborator}
            onChange={vm.setCollaborator}
            renderSearchItem={(props) => (
              <AddCollaboratorSearchItem {...props} />
            )}
            data-test="add-job-collaborator-as-admin-dropdown"
          />
        </FormGroupContainer>
        {vm.modalType !== 'requestingChampion' && (
          <FormRow className={styles.row}>
            <div className={styles.reasonLabel}>Reason</div>
            <TextAreaContainer
              rows={4}
              model={vm}
              field="reason"
              name="reason"
              maxLength={vm.MAX_REASON_LENGTH}
              error={reasonError}
              status={reasonError ? 'danger' : 'default'}
              placeholder={vm.reasonHelpText}
              help={`${vm.reasonCharsRemaining} characters remaining`}
              data-test="add-job-collaborator-as-admin-reason-input"
            />
          </FormRow>
        )}
      </Form>
    </>
  )
})

interface AddCollaboratorSearchItem
  extends RenderSearchItemProps<Member | null> {}

const AddCollaboratorSearchItem = observer(
  React.forwardRef(
    (
      { item, itemToString, ...itemProps }: AddCollaboratorSearchItem,
      ref: React.Ref<any>,
    ) => {
      return (
        <SearchResultItem
          {...itemProps}
          ref={ref}
          visual={<Avatar size={24} subject={item} />}
          data-test={
            'add-job-collaborator-as-admin--option-' +
            item?.user.publicId?.toLowerCase()
          }
        >
          <>
            <div className={styles.collaboratorName}>{item?.displayName}</div>
            <div className={styles.collaboratorEmail}>{item?.user.email}</div>
          </>
        </SearchResultItem>
      )
    },
  ),
)
